import { Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import strings from "../../i18n/i18nStrings";

const debounce = <T extends (...args: any[]) => void>(
  f: T,
  delay: number
) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debouncedFn =  (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      f(...args);
    }, delay);
  };

  debouncedFn.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  return debouncedFn
};

interface SearchFilterProps {
  onChange: (value: string) => void;
}

export const SearchFilter = ({ onChange }: SearchFilterProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const debouncedOnChange = useMemo(() => debounce(onChange, 1000), [onChange])

  const componentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: { target: any }) => {
      if (
        componentRef.current &&
        !componentRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Cleanup function if component unmounts
    // => We don't want onChange to be called in such a case
    return () => {
      debouncedOnChange.cancel()
    }
  }, [debouncedOnChange])

  // useEffect(() => {
  //   if (value) {
  //     onChange(value);
  //   }
  // }, [value, onChange]);

  return (
    <div className="relative" ref={componentRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-semibold text-blue-700 bg-white hover:bg-gray-50"
      >
        <Search className="h-5 w-5 text-blue-700" />
        <span className="hidden md:inline-block">{strings.common.search}</span>
      </button>
      {isOpen && (
        <div className="absolute -left-32 bottom-16 mt-2 w-80 md:w-96 bg-white border border-gray-300 rounded-md shadow-lg z-10">
          <div className="relative">
            <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
              <Search className="w-4 h-4 text-gray-500" />
            </div>
            <input
              type="search"
              id="search"
              className="block w-full p-4 ps-10 text-sm text-gray-900 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Search"
              onChange={(e) => debouncedOnChange(e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
